// src/Layout.jsx - Update the navItems array
import React, { useState, useEffect, useCallback } from 'react'
import { FlyoutMenu, MenuItem, Button, Tooltip, Divider, Chip } from '@dhis2/ui'
import { 
  ChevronDown, User, LogOut, Menu as MenuIcon, ChevronLeft, ChevronRight, 
  Info, Settings, HelpCircle, Sun, Moon, 
  Edit, Phone, UserCog
} from 'lucide-react'
import { useNavigate, NavLink, Outlet, Navigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, Target, NotebookPen, Contact,
  BarChart2, CheckSquare, ListCheck, MessageSquare, ClipboardList
} from 'lucide-react'
import { useDataEngine } from '@dhis2/app-runtime'

import { useAuth } from '../../features/auth/AuthContext'
import { SUPERUSER_ROLE_ID, STAFF_ROLES_ID } from './AccessControl'
// import { showSessionExpiredAlert } from '../../utils/sessionManager'; 
import VerificationRequiredBanner from '../../features/auth/components/VerificationRequiredBanner';
import { useFiscalYear } from '../FiscalYearContext';
import { POSITION_ATTRIBUTES, POSITION_TEI_TYPE } from '../../features/staff/api/constants';



// Define navItems with all navigation items including Check-in
const navItems = [
  { to: "/dashboard", icon: <LayoutDashboard size={24} />, label: "Dashboard", roles: [] },
  { to: "/performance-management", icon: <NotebookPen size={24} />, label: "Objectives Management", roles: [] },
  { to: "/ppn", icon: <CheckSquare size={24} />, label: "Personal Performance Notes", roles: [] },
  { to: "/check-in/dashboard", icon: <ClipboardList size={24} />, label: "Check-in", roles: [] },
  { to: "/leadership-management", icon: <UserCog />, label: "Leadership Management", roles: [STAFF_ROLES_ID.ADMIN, STAFF_ROLES_ID.CEO] },
  { to: "/performance-appraisal/mid-year", icon: <Target size={24} />, label: "Mid-Year Appraisal", roles: [] },
  { to: "/performance-appraisal/year-end", icon: <Target size={24} />, label: "Year-End Appraisal", roles: [] },
  // { to: "/kpis", icon: <BarChart2 size={24} />, label: "KPIs", roles: [] },
  { to: "/tasks", icon: <ListCheck size={24} />, label: "My Reports", roles: [] },
  { to: "/staff", icon: <Contact size={24} />, label: "Staff Management", roles: [STAFF_ROLES_ID.ADMIN, STAFF_ROLES_ID.CEO] },
  { to: "/registration", icon: <Users size={24} />, label: "Users Management", roles: [SUPERUSER_ROLE_ID] },
  { to: "/settings", icon: <Settings size={24} />, label: "System Settings", roles: [STAFF_ROLES_ID.ADMIN, STAFF_ROLES_ID.CEO] },
]

// Program configuration (only for loading the company logo)
const PROGRAM_CONFIG = {
  program: 'NkReudUAW4F',
  stages: {
    fiscalYear: 'htIWgNQWEMm'
  },
  dataElements: {
    companyLogo: 'muysgcGfkyN'
  }
}

const Layout = () => {
  const { user, registrationComplete, logout } = useAuth()
  const navigate = useNavigate()
  const engine = useDataEngine()
  
  // ✅ FISCAL YEAR FROM CONTEXT (single source of truth)
  const { fiscalYear, loading } = useFiscalYear()

  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile)
  const [darkMode, setDarkMode] = useState(false)
  const [notificationCount] = useState(3)
  const [logoError, setLogoError] = useState(false)
  const [companyLogo, setCompanyLogo] = useState(null)

  const [currentUserPosition, setCurrentUserPosition] = useState(null);
  const [loadingPosition, setLoadingPosition] = useState(false);

 
  // ✅ LOAD ONLY COMPANY LOGO (fiscal year is already loaded by context)
  useEffect(() => {
    let cancelled = false

    const loadLogo = async () => {
      if (!user?.organisationUnits?.[0]?.id || !engine) return
      if (!registrationComplete) {
        if (!cancelled) setCompanyLogo(null)
        return
      }

      try {
        const enrollmentsResponse = await engine.query({
          enrollmentsQuery: {
            resource: 'enrollments',
            params: {
              ouMode: 'ACCESSIBLE',
              program: PROGRAM_CONFIG.program,
              fields: 'trackedEntityInstance',
              pageSize: 1,
              order: 'lastUpdated:desc',
            },
          },
        })
        const teiId = enrollmentsResponse.enrollmentsQuery?.enrollments?.[0]?.trackedEntityInstance
        if (!teiId) return

        const eventsResponse = await engine.query({
          eventsQuery: {
            resource: 'events',
            params: {
              program: PROGRAM_CONFIG.program,
              programStage: PROGRAM_CONFIG.stages.fiscalYear,
              ouMode: 'ACCESSIBLE',
              trackedEntityInstance: teiId,
              pageSize: 1,
              order: 'eventDate:desc',
              fields: 'dataValues[dataElement,value]',
            },
          },
        })

        const event = eventsResponse.eventsQuery?.events?.[0]
        if (event && !cancelled) {
          const logo = event.dataValues?.find(
            (dv) => dv.dataElement === PROGRAM_CONFIG.dataElements.companyLogo
          )?.value
          if (logo) setCompanyLogo(logo)
        }
      } catch (error) {
        console.error('❌ Failed to load company logo:', error)
      }
    }

    loadLogo()
    return () => { cancelled = true }
  }, [user?.organisationUnits?.[0]?.id, engine, registrationComplete])

  // Session check (unchanged)
  useEffect(() => {
    if (!user || window.location.pathname === '/login') return;
    
    let intervalId = null;
    
    const checkSession = async () => {
      try {
        const response = await fetch('/api/me', {
          method: 'HEAD',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        
        if (response.ok) {
          console.debug('Session valid - no action needed');
          return;
        }
        
        if (response.status === 401) {
          console.warn('Session expired - showing alert');
         // showSessionExpiredAlert();
        }
      } catch (error) {
        console.debug('Session check network error:', error.message);
      }
    };
    
    intervalId = setInterval(checkSession, 5 * 60 * 1000);
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user]);

  // ✅ Load the user's position record so we can display the actual position title
useEffect(() => {
  let cancelled = false;

  const loadPosition = async () => {
    if (!user?.id || !engine) return;

    setLoadingPosition(true);
    try {
      // Position TEI where "Current Incumbent Staff ID" == user.id
      const response = await engine.query({
        userPosition: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: POSITION_TEI_TYPE,
            filter: `${POSITION_ATTRIBUTES.CURRENT_INCUMBENT}:eq:${user.id}`,
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            ouMode: 'ACCESSIBLE',
            pageSize: 1,
          },
        },
      });

      const position = response.userPosition?.trackedEntityInstances?.[0] || null;

      if (!cancelled) {
        setCurrentUserPosition(position);
      }
    } catch (err) {
      // Non-fatal — fall back to role map below
      console.warn('⚠️ [Layout] Could not load user position:', err?.message);
      if (!cancelled) setCurrentUserPosition(null);
    } finally {
      if (!cancelled) setLoadingPosition(false);
    }
  };

  loadPosition();
  return () => {
    cancelled = true;
  };
}, [user?.id, engine]);

  // Check if current date is within a date range
  const isWithinDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false
    const now = new Date()
    const start = new Date(startDate)
    const end = new Date(endDate)
    return now >= start && now <= end
  }

  // Calculate mid-year period (first 6 months of fiscal year)
  const getMidYearPeriod = useCallback(() => {
    if (!fiscalYear?.start) return { start: null, end: null }
    const start = new Date(fiscalYear.start)
    const end = new Date(start)
    end.setMonth(end.getMonth() + 6)
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    }
  }, [fiscalYear?.start])

  // Calculate end-year period (remaining 6 months of fiscal year)
  const getEndYearPeriod = useCallback(() => {
    if (!fiscalYear?.start || !fiscalYear?.end) return { start: null, end: null }
    const midYearEnd = new Date(fiscalYear.start)
    midYearEnd.setMonth(midYearEnd.getMonth() + 6)
    return {
      start: midYearEnd.toISOString().split('T')[0],
      end: fiscalYear.end
    }
  }, [fiscalYear?.start, fiscalYear?.end])

  // Check if mid-year appraisal should be visible
  const showMidYearAppraisal = useCallback(() => {
    if (loading) return false
    if (!fiscalYear?.start) return false
    const midYear = getMidYearPeriod()
    return isWithinDateRange(midYear.start, midYear.end)
  }, [loading, fiscalYear?.start, getMidYearPeriod])

  // Check if year-end appraisal should be visible
  const showYearEndAppraisal = useCallback(() => {
    if (loading) return false
    if (!fiscalYear?.start || !fiscalYear?.end) return false
    const endYear = getEndYearPeriod()
    return isWithinDateRange(endYear.start, endYear.end)
  }, [loading, fiscalYear?.start, fiscalYear?.end, getEndYearPeriod])

  // Check if user has manager/HR role for Check-in access
  const hasCheckInAccess = useCallback(() => {
    // Now everyone can access check-in
    return true;
  }, []);;

  // Filter nav items based on roles AND date conditions
  const getFilteredNavItems = useCallback(() => {
    return navItems.filter(item => {
      // Role-based filtering
      if (item.roles.length > 0) {
        if (!user?.authorities) return false
        if (!item.roles.some(r => user.authorities.includes(r))) return false
      }
      // Date-based filtering for appraisal items
      if (item.to === "/performance-appraisal/mid-year") {
        return showMidYearAppraisal()
      }
      if (item.to === "/performance-appraisal/year-end") {
        return showYearEndAppraisal()
      }
      // Check-in is now always visible
      return true
    })
  }, [user, showMidYearAppraisal, showYearEndAppraisal])

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) setSidebarOpen(false)
      if (!mobile) setSidebarOpen(true)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Clean up blob URLs
  useEffect(() => {
    return () => {
      if (companyLogo && companyLogo.startsWith('blob:')) {
        URL.revokeObjectURL(companyLogo)
      }
    }
  }, [companyLogo])

  const toggleDarkMode = () => {
    setDarkMode(!darkMode)
  }

  // Extract a value from a TEI attributes array
  const getPositionAttribute = (position, attributeId) => {
    if (!position?.attributes) return null;
    return position.attributes.find((a) => a.attribute === attributeId)?.value || null;
  };

  const positionTitle = getPositionAttribute(currentUserPosition, POSITION_ATTRIBUTES.POSITION_TITLE);

  const getDisplayTitle = () => {
    // Show loading state
    if (loadingPosition) {
      return 'Loading position...';
    }
    
    // Check if position exists but title is missing
    if (currentUserPosition && !getPositionAttribute(currentUserPosition, POSITION_ATTRIBUTES.POSITION_TITLE));
    const positionTitle = getPositionAttribute(currentUserPosition, POSITION_ATTRIBUTES.POSITION_TITLE);
    if (positionTitle) return positionTitle;
    
    // No position found
    return 'Position not set';
  };

  const linkStyle = {
    color: 'white',
    textDecoration: 'none',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
    whiteSpace: 'nowrap',
    borderRadius: '6px',
    fontSize: "14px",
  }

  const activeLinkStyle = {
    color: '#0ae315ff',
    backgroundColor: 'rgba(255,204,0,0.15)'
  }

  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false)
  }

  const menuStyles = {
    menuContainer: {
      minWidth: '320px',
      maxWidth: '400px',
    },
    userSection: {
      padding: '16px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    userName: {
      margin: '0 0 4px 0',
      fontSize: '18px',
      fontWeight: 600,
    },
    userEmail: {
      margin: 0,
      fontSize: '14px',
      opacity: 0.9,
    },
    infoField: {
      marginBottom: '8px',
    },
    fieldLabel: {
      fontSize: '11px',
      opacity: 0.8,
      marginBottom: '2px',
    },
    fieldValue: {
      fontSize: '13px',
      fontWeight: 500,
    },
    badge: {
      backgroundColor: '#ef4444',
      color: 'white',
      fontSize: '11px',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: '12px',
      marginLeft: '8px',
    }
  }

  // Debug log – uses context values
  useEffect(() => {
    if (!loading && fiscalYear?.start && fiscalYear?.end) {
      const midYear = getMidYearPeriod()
      const endYear = getEndYearPeriod()
      console.log('📅 Fiscal Year (from context):', fiscalYear)
      console.log('📅 Mid-Year Period:', midYear)
      console.log('📅 End-Year Period:', endYear)
      console.log('📅 Show Mid-Year:', showMidYearAppraisal())
      console.log('📅 Show End-Year:', showYearEndAppraisal())
      console.log('📅 Current Date:', new Date().toISOString().split('T')[0])
    }
  }, [loading, fiscalYear, getMidYearPeriod, getEndYearPeriod, showMidYearAppraisal, showYearEndAppraisal])

  // ---------- JSX (exactly as your original, no changes) ----------
  return (
    <>
      <VerificationRequiredBanner />
      <style>{`
        @media print {
          html,
          body,
          #root {
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
          }

          .app-layout,
          .app-layout__content,
          .app-layout__main {
            display: block !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
            max-height: none !important;
          }

          .app-layout__sidebar,
          .app-layout__header {
            display: none !important;
          }

          .app-layout__content {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .app-layout__main {
            padding: 0 !important;
          }

          .app-layout__main > * {
            overflow: visible !important;
          }
        }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .menu-dropdown {
            animation: slideIn 0.2s ease-out;
          }
      `}
      </style>
      
      <div 
        className="app-layout" 
        style={{ 
          display: 'flex', 
          height: '100vh', 
          fontFamily: 'sans-serif',
          background: '#f0f2f5',
          padding: isMobile ? '0' : '16px', 
          boxSizing: 'border-box',
          gap: isMobile ? '0' : '16px', 
          overflow: 'hidden'
        }}
      >
        {/* Mobile Overlay Backdrop - Closes sidebar when clicked */}
        {isMobile && sidebarOpen && (
          <div 
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 250,
              transition: 'opacity 0.3s ease'
            }}
          />
        )}

        {/* Sidebar */}
        <aside
          className="app-layout__sidebar"
          style={{
            width: sidebarOpen ? '280px' : '60px',
            background: '#003366',
            color: 'white',
            padding: sidebarOpen ? '1rem' : '0.5rem',
            display: isMobile ? (sidebarOpen ? 'flex' : 'none') : 'flex',
            flexDirection: 'column',
            gap: '1rem',
            position: isMobile ? 'fixed' : 'static',
            top: isMobile ? '0' : 'auto',
            left: isMobile ? '0' : 'auto',
            height: isMobile ? '100vh' : 'auto',
            minHeight: isMobile ? '100vh' : 0,
            zIndex: isMobile ? 300 : 'auto',
            transition: 'width 0.3s ease, padding 0.3s ease, transform 0.3s ease',
            boxShadow: isMobile 
              ? (sidebarOpen ? '4px 0 24px rgba(0,0,0,0.3)' : 'none') 
              : '0 4px 12px rgba(0,0,0,0.15)',
            overflowX: 'hidden',
            overflowY: 'auto',
            borderRadius: isMobile ? '0' : '18px',
            border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.1)',
            flexShrink: 0,
          }}
        >
          {/* Header with Logo */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebarOpen ? 'space-between' : 'center',
              marginBottom: sidebarOpen ? '16px' : '0',
              minHeight: '60px',
              flexShrink: 0
            }}
          >
            {sidebarOpen ? (
              <>
                {companyLogo && !logoError ? (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    width: '100%',
                    justifyContent: 'space-between'
                  }}>
                    <img 
                      src={companyLogo} 
                      alt="Company Logo" 
                      style={{ 
                        maxHeight: '60px',
                        maxWidth: '180px',
                        width: 'auto',
                        objectFit: 'contain',
                        backgroundColor: 'white',
                        padding: '6px',
                        borderRadius: '6px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onError={() => setLogoError(true)}
                    />
                    <Button
                      small
                      icon={<ChevronLeft size={18} />}
                      onClick={() => setSidebarOpen(false)}
                      style={{ background: 'transparent', border: 'none', color: 'white' }}
                      title="Collapse Sidebar"
                    />
                  </div>
                ) : (
                  <>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: 'white' }}>Company</span>
                    <Button
                      small
                      icon={<ChevronLeft size={18} />}
                      onClick={() => setSidebarOpen(false)}
                      style={{ background: 'transparent', border: 'none', color: 'white' }}
                      title="Collapse Sidebar"
                    />
                  </>
                )}
              </>
            ) : (
              <Button
                small
                icon={<ChevronRight size={18} />}
                onClick={() => setSidebarOpen(true)}
                style={{ background: 'transparent', border: 'none', color: 'white', padding: '8px' }}
                title="Expand Sidebar"
              />
            )}
          </div>

          {/* Navigation Items - Scrollable */}
          <nav style={{ 
            flex: 1, 
            overflowY: 'auto', 
            overflowX: 'hidden',
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0.5rem',
            minHeight: 0,
            scrollbarWidth: isMobile ? 'none' : 'auto',
            msOverflowStyle: isMobile ? 'none' : 'auto'
          }}>
            {registrationComplete &&
              getFilteredNavItems().map(({ to, icon, label }) => {
                if (!sidebarOpen) {
                  return (
                    <Tooltip dense content={label} key={to} placement="right">
                      <NavLink
                        to={to}
                        style={({ isActive }) => ({
                          ...linkStyle,
                          justifyContent: 'center',
                          ...(isActive ? activeLinkStyle : {}),
                          paddingLeft: 0,
                          paddingRight: 0,
                        })}
                        onClick={handleNavClick}
                      >
                        {icon}
                      </NavLink>
                    </Tooltip>
                  )
                }
                return (
                  <NavLink
                    key={to}
                    to={to}
                    style={({ isActive }) => ({
                      ...linkStyle,
                      ...(isActive ? activeLinkStyle : {}),
                    })}
                    onClick={handleNavClick}
                  >
                    {icon}
                    {label}
                  </NavLink>
                )
              })}

            {loading && sidebarOpen && (
              <div style={{ color: 'white', padding: '8px', fontSize: '12px' }}>
                Loading calendar...
              </div>
            )}
          </nav>
        </aside>

        {/* Content Card */}
        <div
          className="app-layout__content-card"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
            borderRadius: isMobile ? '0' : '18px', 
            boxShadow: isMobile ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: isMobile ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
            height: isMobile ? '100vh' : 'auto',
            minHeight: 0
          }}
        >
            {/* Header */}
          <header
            className="app-layout__header"
            style={{
              background: '#ffffff',
              padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              zIndex: 1100,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
              boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
            }}
          >
            {/* Left side - Hamburger ALWAYS visible on mobile */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isMobile && (
                <Button
                  small
                  icon={<MenuIcon size={20} />}
                  onClick={() => setSidebarOpen(true)}
                  style={{ 
                    marginRight: '4px',
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#333'
                  }}
                  title="Open Menu"
                />
              )}

              {/* Mobile logo - only when sidebar is closed */}
              {isMobile && !sidebarOpen && companyLogo && !logoError && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '40px',
                  maxWidth: '120px'
                }}>
                  <img 
                    src={companyLogo} 
                    alt="Company Logo" 
                    style={{ 
                      maxHeight: '40px',
                      maxWidth: '120px',
                      width: 'auto',
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      padding: '4px',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onError={() => setLogoError(true)}
                  />
                </div>
              )}

              {/* Page title */}
              {(!isMobile || sidebarOpen) && (
                <h1 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', color: 'green' }}>
                  Performance Management System
                </h1>
              )}
            </div>

            {/* Right side - User menu, notifications, help */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: isMobile ? '8px' : '12px',
              minWidth: '150px',
              position: 'relative',
            }}>
              {/* User menu */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpen(prev => !prev)}
                  style={{
                    display: 'flex',
                    backgroundColor: '#078b14ff',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '22px',
                    padding: isMobile ? '4px 8px' : '6px 10px',
                    cursor: 'pointer',
                    color: 'white',
                    boxShadow: '0 8px 12px rgba(0,0,0,0.3)',
                    minWidth: isMobile ? '100px' : '120px',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <User size={isMobile ? 18 : 20} />
                    <span style={{ fontSize: isMobile ? '13px' : '14px' }}>
                      {user?.name?.split(' ')[0] || 'User'}
                    </span>
                  </div>
                  <ChevronDown size={14} style={{ 
                    transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} />
                </button>

                {menuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    zIndex: 9999,
                    minWidth: '380px',
                    width: isMobile ? 'calc(100vw - 32px)' : 'auto',
                    maxWidth: isMobile ? '320px' : 'none'
                  }}>
                    <FlyoutMenu style={{ 
                      ...menuStyles.menuContainer, 
                      width: window.innerWidth < 768 ? '90vw' : '480px',
                      maxWidth: '480px',
                      maxHeight: '85vh',
                      overflowY: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                    }}>
                      {/* Header with Close Button */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 20px',
                        borderBottom: '1px solid #e5e7eb',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '16px 16px 0 0'
                      }}>
                        <div style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>
                          Account
                        </div>
                        <button
                          onClick={() => setMenuOpen(false)}
                          style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            borderRadius: '6px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: '16px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                        >
                          ✕
                        </button>
                      </div>

                      {/* User Info Section - Compact */}
                      <div style={{
                        padding: '16px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px',
                            fontWeight: 'bold',
                            color: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            border: '2px solid rgba(255,255,255,0.3)'
                          }}>
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <h3 style={{
                              margin: '0 0 2px 0',
                              fontSize: '15px',
                              fontWeight: 600,
                              color: 'white'
                            }}>{user?.name || 'User'}</h3>
                            <p style={{
                              margin: 0,
                              fontSize: '11px',
                              opacity: 0.9,
                              color: 'white'
                            }}>
                              {user?.email || 'No email set'}
                            </p>
                          </div>
                        </div>

                        {/* 2-column compact info */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(1fr)',
                          gap: '8px',
                          marginTop: '12px',
                        }}>
                          <div style={{
                            background: 'rgba(255,255,255,0.12)',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            minWidth: 0,                        // ✅
                          }}>
                            <div style={{ fontSize: '9px', opacity: 0.8, marginBottom: '2px' }}>Department</div>
                            <div
                              title={user?.organisationUnits?.[0]?.name || 'N/A'}
                              style={{
                                fontSize: '11px',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user?.organisationUnits?.[0]?.name || 'N/A'}
                            </div>
                          </div>

                        <div style={{
                            background: 'rgba(255,255,255,0.12)',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            minWidth: 0,                        // ✅
                          }}>
                            <div title={getDisplayTitle()}
                              style={{
                                fontSize: '11px',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {loadingPosition && !currentUserPosition
                                ? 'Loading…'
                                : getDisplayTitle()}
                            </div>
                          </div>
                        </div>

                        {/* Username chip */}
                        {user?.userCredentials?.username && (
                          <div style={{
                            marginTop: '8px',
                            background: 'rgba(255,255,255,0.08)',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '10px',
                            display: 'inline-block'
                          }}>
                            @{user.userCredentials.username}
                          </div>
                        )}
                      </div>

                      <Divider style={{ margin: 0 }} />

                      {/* 3-Column Grid Sections */}
                      <div style={{ padding: '12px 16px' }}>
                        {/* Profile Section - 3 columns */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: window.innerWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                          gap: '6px',
                          marginBottom: '12px'
                        }}>
                          <MenuItem
                            label="Edit"
                            icon={<Edit size={14} />}
                            onClick={() => {
                              navigate('/profile')
                              setMenuOpen(false)
                            }}
                            dense
                            style={{ 
                              textAlign: 'center', 
                              justifyContent: 'center',
                              borderRadius: '6px',
                              fontSize: '12px',
                              padding: '6px 4px'
                            }}
                          />
                          <MenuItem
                            label="Copy Phone"
                            icon={<Phone size={14} />}
                            onClick={() => {
                              if (user?.phoneNumber) {
                                navigator.clipboard.writeText(user.phoneNumber);
                                alert(`Phone number copied: ${user.phoneNumber}`);
                              }
                              setMenuOpen(false)
                            }}
                            dense
                            style={{ 
                              textAlign: 'center', 
                              justifyContent: 'center',
                              borderRadius: '6px',
                              fontSize: '12px',
                              padding: '6px 4px',
                              opacity: user?.phoneNumber ? 1 : 0.5
                            }}
                          />
                          <MenuItem
                            label="Messages"
                            icon={<MessageSquare size={14} />}
                            onClick={() => {
                              navigate('/s')
                              setMenuOpen(false)
                            }}
                            dense
                            style={{ 
                              textAlign: 'center', 
                              justifyContent: 'center',
                              borderRadius: '6px',
                              fontSize: '12px',
                              padding: '6px 4px'
                            }}
                          />
                        </div>

                        <Divider style={{ margin: '8px 0' }} />

                        {/* Settings & Theme - 3 columns */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: window.innerWidth < 768 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                          gap: '6px',
                          marginBottom: '12px'
                        }}>
                          <MenuItem
                            label="Settings"
                            icon={<Settings size={14} />}
                            onClick={() => {
                              navigate('/settings')
                              setMenuOpen(false)
                            }}
                            dense
                            style={{ 
                              textAlign: 'center', 
                              justifyContent: 'center',
                              borderRadius: '6px',
                              fontSize: '12px',
                              padding: '6px 4px'
                            }}
                          />
                          <MenuItem
                            label={darkMode ? 'Light' : 'Dark'}
                            icon={darkMode ? <Sun size={14} /> : <Moon size={14} />}
                            onClick={() => {
                              toggleDarkMode()
                              setMenuOpen(false)
                            }}
                            dense
                            style={{ 
                              textAlign: 'center', 
                              justifyContent: 'center',
                              borderRadius: '6px',
                              fontSize: '12px',
                              padding: '6px 4px'
                            }}
                          />
                          <MenuItem
                            label="Help"
                            icon={<HelpCircle size={14} />}
                            onClick={() => {
                              window.open('https://pms.kakobrands.com/PMS_END_USER_MANUAL.pdf', '_blank')
                              setMenuOpen(false)
                            }}
                            dense
                            style={{ 
                              textAlign: 'center', 
                              justifyContent: 'center',
                              borderRadius: '6px',
                              fontSize: '12px',
                              padding: '6px 4px'
                            }}
                          />
                        </div>

                        <Divider style={{ margin: '8px 0' }} />

                        {/* Logout - Full width */}
                        <MenuItem
                          label={
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center', 
                              gap: '8px',
                              width: '100%',
                              color: 'red'
                            }}>
                              <LogOut size={14} />
                              <span>Sign Out</span>
                            </div>
                          }
                          onClick={async () => {
                            setMenuOpen(false)
                            await logout()
                          }}
                          dense
                          style={{ 
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            borderRadius: '6px',
                            marginTop: '4px',
                            textAlign: 'center',
                            justifyContent: 'center'
                          }}
                        />
                      </div>

                      {/* Footer - Compact */}
                      <div style={{
                        padding: '8px 16px',
                        fontSize: '10px',
                        color: '#666',
                        borderTop: '1px solid #eee',
                        background: '#f9fafb',
                        borderRadius: '0 0 16px 16px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: '#10b981',
                              animation: 'pulse 2s infinite'
                            }}></div>
                            <span>Online</span>
                          </div>
                          <div>v1.1.4</div>
                          <div>© 2026</div>
                        </div>
                      </div>
                    </FlyoutMenu>
                  </div>
                )}
              </div>

              {/* Help/Info button */}
              {!isMobile && (
                <button
                  onClick={() => window.open('https://pms.kakobrands.com/PMS_END_USER_MANUAL.pdf', '_blank')}
                  style={{
                    display: 'flex',
                    backgroundColor: '#e9a20aff',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '22px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    color: 'white',
                    boxShadow: '0 8px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  <Info size={24} />
                </button>
              )}
            </div>
          </header>

          {/* Main content - Scrollable */}
          <main
            className="app-layout__main"
            style={{
              padding: registrationComplete ? (isMobile ? '1rem' : '2rem') : '0',
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              background: '#ffffff',
              minHeight: 0
            }}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </>
  )
}

export default Layout
